import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/**
 * Tiny status badge + "Warm cache" button + disk telemetry tooltip for
 * the Research-Window Kline Cache (`/api/research-windows/*` +
 * `/api/research-cache/telemetry`). Rendered next to the Research-
 * Window dropdown on Backtest and Walk-Forward.
 *
 * Pure UX. ZERO impact on backtest/strategy/live trading code paths.
 */
export function ResearchCachePill({ presetId, strategyProfileId, symbol = "BTCUSDT", testidPrefix = "bt" }) {
  const [status, setStatus] = useState(null);
  const [telemetry, setTelemetry] = useState(null);
  const [busy, setBusy] = useState(false);
  const tid = (k) => `${testidPrefix}-research-cache-${k}`;

  const refresh = useCallback(async () => {
    if (!presetId || presetId === "custom") {
      setStatus(null);
      setTelemetry(null);
      return;
    }
    try {
      const [s, t] = await Promise.all([
        api.researchWindowsCacheStatus(presetId, {
          symbol,
          strategy_profile_id: strategyProfileId,
        }),
        api.researchCacheTelemetry().catch(() => null),
      ]);
      setStatus(s);
      setTelemetry(t);
    } catch (e) {
      setStatus({ status: "error", error: e?.response?.data?.detail || e.message });
    }
  }, [presetId, strategyProfileId, symbol]);

  useEffect(() => { refresh(); }, [refresh]);

  const onWarm = async () => {
    if (!presetId || presetId === "custom") return;
    if (!strategyProfileId && !(status?.timeframes_requested?.length)) {
      toast.error("Pick a strategy profile first so the warmer knows which timeframes to fetch.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.researchWindowsWarmCache({
        preset_id: presetId,
        strategy_profile_id: strategyProfileId,
        symbol,
      });
      toast.success(
        `Cache warmed: fetched=${r.fetched_count} skipped=${r.skipped_count}` +
        (r.failed?.length ? ` failed=${r.failed.length}` : ""),
      );
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!presetId || presetId === "custom") return null;

  const s = status?.status || "empty";
  const colour = {
    cold:    "text-[#71717A]  border-[#71717A]/40",
    partial: "text-[#F59E0B]  border-[#F59E0B]/50",
    ready:   "text-[#00E676]  border-[#00E676]/50",
    empty:   "text-[#52525B]  border-[#52525B]/40",
    error:   "text-[#FF3B30]  border-[#FF3B30]/50",
  }[s] || "text-[#71717A] border-[#71717A]/40";
  const cached = status?.cached_cells ?? 0;
  const total  = status?.total_cells ?? 0;

  // Disk telemetry summary string — surfaces hit ratio + size + time saved.
  const disk = telemetry?.disk;
  const tooltipLines = [];
  if (disk) {
    const ratioPct = (disk.kline_hit_ratio * 100).toFixed(0);
    tooltipLines.push(`Disk hits ${disk.disk_kline_hits} / misses ${disk.disk_kline_misses} (${ratioPct}%)`);
    tooltipLines.push(`Files ${disk.cache_files} · ${disk.cache_size_mb} MB`);
    if (disk.estimated_time_saved_ms > 0) {
      const s = (disk.estimated_time_saved_ms / 1000).toFixed(1);
      tooltipLines.push(`Saved ≈ ${s}s of fetch time`);
    }
    tooltipLines.push(`Schema v${disk.schema_version} · indicators v${disk.indicator_version}`);
    if (!disk.has_parquet) tooltipLines.push("(Parquet unavailable — JSON gzip fallback)");
  }

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={tid("wrapper")}>
      <span
        data-testid={tid("status-pill")}
        className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 border rounded-sm ${colour}`}
        title={status?.error || tooltipLines.join("\n") || `Klines cache for ${status?.preset_label || presetId}`}
      >
        Cache {s}{total > 0 ? ` ${cached}/${total}` : ""}
      </span>
      {disk ? (
        <span
          data-testid={tid("disk-telemetry")}
          className="text-[10px] font-mono text-[#71717A]"
          title={tooltipLines.join("\n")}
        >
          {disk.disk_kline_hits}h / {disk.disk_kline_misses}m · {disk.cache_size_mb}MB
        </span>
      ) : null}
      <Button
        type="button"
        variant="outline"
        size="sm"
        data-testid={tid("warm-button")}
        disabled={busy || s === "ready"}
        onClick={onWarm}
        className="h-6 px-2 text-[10px] uppercase tracking-widest rounded-sm bg-[#1A1A22] border-border hover:bg-[#262633] disabled:opacity-40"
        title={s === "ready" ? "Cache already warm" : "Pre-fetch klines for this preset"}
      >
        {busy ? "Warming…" : "Warm cache"}
      </Button>
    </div>
  );
}

export default ResearchCachePill;

